import { IBalusterGeometry, IRailingGeometry, IVec3 } from '@stair-craft/common/util';

import { clearBalusterSpacing } from '../shared-baluster-count/baluster-count';
import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';
import { gapFillers } from '../shared-settle-railings/rail-gaps';

/** A stair's top post and an opening railing's post closer than this stand as one (mm). */
const JUNCTION_REACH: number = 300;

/** Points closer than this count as one place (mm). */
const SAME_PLACE: number = 0.5;

interface IJunction {
    readonly upper: IRailingGeometry;
    readonly post: IBalusterGeometry;
    readonly distance: number;
}

function planDistance(a: IPlanPoint, b: IPlanPoint): number {
    return Math.hypot(a.x - b.x, a.z - b.z);
}

function unit(from: IPlanPoint, to: IPlanPoint): IPlanPoint {
    const length: number = planDistance(from, to);
    return length < SAME_PLACE ? { x: 0, z: 0 } : { x: (to.x - from.x) / length, z: (to.z - from.z) / length };
}

/** The point of the line through `a` along `direction` nearest to `at`. */
function nearestOnLine(a: IPlanPoint, direction: IPlanPoint, at: IPlanPoint): IPlanPoint {
    const t: number = (at.x - a.x) * direction.x + (at.z - a.z) * direction.z;
    return { x: a.x + direction.x * t, z: a.z + direction.z * t };
}

/** The post of a railing at its top handrail end, if one stands there. */
function topPost(railing: IRailingGeometry): IBalusterGeometry | undefined {
    const end: IVec3 | undefined = railing.handrail.points.at(-1);
    if (!end) {
        return undefined;
    }
    return railing.posts.find((post: IBalusterGeometry) => planDistance(post.position, end) <= railing.postWidth);
}

/** The opening railing post nearest to a room point, within the junction reach. */
function nearestUpperPost(upperRailings: readonly IRailingGeometry[], at: IPlanPoint): IJunction | undefined {
    let best: IJunction | undefined;
    for (const upper of upperRailings) {
        for (const post of upper.posts) {
            const distance: number = planDistance(post.position, at);
            if (distance < JUNCTION_REACH && (!best || distance < best.distance)) {
                best = { upper, post, distance };
            }
        }
    }
    return best;
}

/**
 * The opening handrail end at the dropped post, run on to the face of the stair post at `stair`:
 * along its own line to the point opposite the stair post, then across to it when the post stands
 * off that line. Returned from the end outward, at the end's height.
 */
function legsToStairPost(end: IVec3, inward: IVec3, stair: IPlanPoint, half: number): IVec3[] {
    const outward: IPlanPoint = unit(inward, end);
    const turn: IPlanPoint = nearestOnLine(end, outward, stair);
    const level: (point: IPlanPoint) => IVec3 = (point: IPlanPoint): IVec3 => ({ x: point.x, y: end.y, z: point.z });
    if (planDistance(turn, stair) < half) {
        return [level({ x: stair.x - outward.x * half, z: stair.z - outward.z * half })];
    }
    const across: IPlanPoint = unit(turn, stair);
    return [level(turn), level({ x: stair.x - across.x * half, z: stair.z - across.z * half })];
}

/**
 * Drops the opening post next to the stair's top post and brings the opening handrail to the
 * stair post's face. The gap the move opened is closed with balusters on the slab.
 */
function joinAtJunction(railing: IRailingGeometry, upperRailings: readonly IRailingGeometry[], placement: IPlanPoint, limit: number): void {
    const post: IBalusterGeometry | undefined = topPost(railing);
    const stair: IPlanPoint | undefined = post && { x: post.position.x + placement.x, z: post.position.z + placement.z };
    const junction: IJunction | undefined = stair && nearestUpperPost(upperRailings, stair);
    if (!post || !stair || !junction || junction.upper.handrail.points.length < 2) {
        return;
    }
    const { upper } = junction;
    const points: IVec3[] = upper.handrail.points;
    const atStart: boolean =
        planDistance(points[0], junction.post.position) < planDistance(points[points.length - 1], junction.post.position);
    const ordered: IVec3[] = atStart ? [...points].reverse() : points;
    const end: IVec3 = ordered[ordered.length - 1];
    const legs: IVec3[] = legsToStairPost(end, ordered[ordered.length - 2], stair, railing.postWidth / 2);
    const joined: IVec3[] = [...ordered.slice(0, -1), ...legs];
    if (atStart) {
        joined.reverse();
    }
    upper.handrail.points = joined;
    upper.posts = upper.posts.filter((other: IBalusterGeometry) => other !== junction.post);
    // The stair post closes the opening rail for the gap count; it stays in the stair railing only.
    const stairPost: IBalusterGeometry = { ...post, position: { x: stair.x, y: junction.post.position.y, z: stair.z } };
    upper.posts.push(stairPost);
    const foot: number = junction.post.position.y;
    upper.balusters.push(...gapFillers(upper, limit, (): number => foot));
    upper.posts = upper.posts.filter((other: IBalusterGeometry) => other !== stairPost);
}

/**
 * One post where a stair railing reaches the railing around the opening: the opening post next to
 * the stair's top post is dropped, and the opening handrail runs on to the stair post's face — along
 * its own line, then across when the stair post stands off it. The stair post stays: on a тетива it
 * holds the board's end. Stair railings are in the stair's frame, opening railings in the room's;
 * `placement` is the stair's (0, 0) in the room.
 */
export function shareJunctionPosts(
    stairRailings: readonly IRailingGeometry[],
    upperRailings: readonly IRailingGeometry[],
    placement: IPlanPoint,
    balusterSpacing: number
): void {
    const limit: number = clearBalusterSpacing(balusterSpacing);
    for (const railing of stairRailings) {
        joinAtJunction(railing, upperRailings, placement, limit);
    }
}
